import React, { useState } from 'react';
import { X, Calendar, MapPin, Clock, Users, Home, Plane, ListOrdered, Edit3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { gameService, type Game, type LineupSource } from '../../services/gameService';

interface CreateGameWizardProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onGameCreated: (game: Game) => void;
}

export const CreateGameWizard: React.FC<CreateGameWizardProps> = ({
  isOpen,
  onClose,
  teamId,
  onGameCreated,
}) => {
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [opponentName, setOpponentName] = useState<string>('');
  const [homeAway, setHomeAway] = useState<'home' | 'away'>('home');
  const [location, setLocation] = useState<string>('');
  const [gameTime, setGameTime] = useState<string>('');
  const [season, setSeason] = useState<string>('');
  const [lineupSource, setLineupSource] = useState<LineupSource>('depth_chart');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (!opponentName.trim()) {
      toast.error('Please enter an opponent name');
      return;
    }

    setSaving(true);
    try {
      const game = await gameService.createGame({
        team_id: teamId,
        date,
        opponent_name: opponentName.trim(),
        home_away: homeAway,
        location: location.trim() || undefined,
        game_time: gameTime || undefined,
        season: season.trim() || undefined,
        lineup_source: lineupSource,
      });

      toast.success('Game created successfully');
      
      // Reset form
      setDate(new Date().toISOString().split('T')[0]);
      setOpponentName('');
      setHomeAway('home');
      setLocation('');
      setGameTime('');
      setSeason('');
      setLineupSource('depth_chart');
      
      onGameCreated(game);
      onClose();
    } catch (error: any) {
      console.error('Error creating game:', error);
      toast.error(error?.message || 'Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create New Game</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-2" />
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* Opponent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline w-4 h-4 mr-2" />
              Opponent Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={opponentName}
              onChange={(e) => setOpponentName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter opponent team name"
              required
            />
          </div>

          {/* Home/Away */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Home className="inline w-4 h-4 mr-2" />
              Home/Away <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="homeAway"
                  value="home"
                  checked={homeAway === 'home'}
                  onChange={(e) => setHomeAway(e.target.value as 'home' | 'away')}
                  className="mr-2"
                />
                <Home className="w-4 h-4 mr-1" />
                Home
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="homeAway"
                  value="away"
                  checked={homeAway === 'away'}
                  onChange={(e) => setHomeAway(e.target.value as 'home' | 'away')}
                  className="mr-2"
                />
                <Plane className="w-4 h-4 mr-1" />
                Away
              </label>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline w-4 h-4 mr-2" />
              Location (Optional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter game location or venue"
            />
          </div>

          {/* Game Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline w-4 h-4 mr-2" />
              Game Time (Optional)
            </label>
            <input
              type="time"
              value={gameTime}
              onChange={(e) => setGameTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Season */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Season (Optional)
            </label>
            <input
              type="text"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., 2024, Spring 2024"
            />
          </div>

          {/* Lineup for this game */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lineup for this game
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Use your default depth chart lineup, or set the batting order manually for this game.
            </p>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer flex-1 border rounded-lg p-3 border-gray-200 hover:bg-gray-50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="lineupSource"
                  value="depth_chart"
                  checked={lineupSource === 'depth_chart'}
                  onChange={() => setLineupSource('depth_chart')}
                  className="mr-3"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <ListOrdered className="w-4 h-4 text-primary" />
                    Use default depth chart
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Lineup from your Depth Chart (Lineup unit)</div>
                </div>
              </label>
              <label className="flex items-center cursor-pointer flex-1 border rounded-lg p-3 border-gray-200 hover:bg-gray-50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="lineupSource"
                  value="manual"
                  checked={lineupSource === 'manual'}
                  onChange={() => setLineupSource('manual')}
                  className="mr-3"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <Edit3 className="w-4 h-4 text-primary" />
                    Set lineup manually
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Choose batting order on the game page</div>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : 'Create Game'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

